/**
 * Admin UI for marketplace ops alert settings (W15-E16).
 */

import React from "react";
import { Alert, Form, InputNumber, Space, Switch, Typography } from "antd";
import PricingFieldHelpBubble from "./PricingFieldHelpBubble.jsx";
import { getMarketplaceFieldHelp } from "../utils/marketplaceSettingsHelp.js";

const { Text } = Typography;

export const NOTIFY_ON_INTEREST_KEY = "marketplace.ops.notifyOnInterest";
export const DEADLINE_WARNING_HOURS_KEY = "marketplace.ops.deadlineWarningHours";

/**
 * @param {object} props
 * @param {object} props.setting - notifyOnInterest setting row
 */
const MarketplaceOpsAlertsField = ({ setting }) => {
  const notifyEnabled = Form.useWatch(NOTIFY_ON_INTEREST_KEY);
  const warningHours = Form.useWatch(DEADLINE_WARNING_HOURS_KEY);
  const notifyHelp = getMarketplaceFieldHelp(NOTIFY_ON_INTEREST_KEY);
  const deadlineHelp = getMarketplaceFieldHelp(DEADLINE_WARNING_HOURS_KEY);

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="Marketplace ops alerts"
        description={
          <>
            Email and in-app notifications when providers respond or when a
            selection deadline is approaching. Recipients use{" "}
            <Text code>admin.email</Text> / <Text code>ADMIN_EMAIL</Text>.
            Deadline warnings also require{" "}
            <Text code>ENABLE_PROVIDER_OPPORTUNITY_DEADLINE_WARNING_WORKER=true</Text>{" "}
            on the backend.
          </>
        }
      />

      <Form.Item
        name={NOTIFY_ON_INTEREST_KEY}
        valuePropName="checked"
        label={
          <Space size="small" align="center">
            <span>Email admin when provider responds</span>
            {notifyHelp && <PricingFieldHelpBubble help={notifyHelp} />}
          </Space>
        }
        extra={
          setting?.description ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {setting.description}
            </Text>
          ) : null
        }
      >
        <Switch
          checkedChildren="On"
          unCheckedChildren="Off"
          aria-label="Notify admin when provider responds to a job"
        />
      </Form.Item>

      {!notifyEnabled ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Response alerts are off — ops must check the order Provider selection
          panel manually.
        </Text>
      ) : null}

      <Form.Item
        name={DEADLINE_WARNING_HOURS_KEY}
        label={
          <Space size="small" align="center">
            <span>Deadline warning lead time (hours)</span>
            {deadlineHelp && <PricingFieldHelpBubble help={deadlineHelp} />}
          </Space>
        }
        extra="Hours before ProviderOpportunity selectionDeadlineAt to email admins a warning (once per opportunity)."
      >
        <InputNumber
          min={1}
          max={168}
          step={1}
          style={{ width: "100%" }}
          addonAfter="hours"
          aria-label="Deadline warning lead time in hours"
        />
      </Form.Item>

      {warningHours != null && Number(warningHours) > 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Ops will be warned about {warningHours} hour
          {Number(warningHours) === 1 ? "" : "s"} before each open opportunity
          deadline (if the backend worker is enabled).
        </Text>
      ) : null}
    </Space>
  );
};

export default MarketplaceOpsAlertsField;
